import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  CheckCircle,
  ChevronDown,
  FileCheck,
  FolderOpen,
  Home,
  MailPlus,
  MessageSquare,
  Pencil,
  Receipt,
  Save,
  Send,
  UserPlus,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { AppDialogFrame } from '../../../components/overlays/AppDialogFrame';
import { ModuleSectionNav, type ModuleSectionNavItem } from '../../../components/navigation/ModuleSectionNav';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import {
  useGetFinancialAidApplicationForReviewQuery,
  useGetFinancialAidStatusHistoryQuery,
  useGetSettingsModuleConfigQuery,
  type FinancialAidReviewDetail,
  type FinancialAidStatusHistoryResponse,
} from '../../../state/domainRouterApi';
import {
  useAssignFinancialAidApplication,
  useMakeFinancialAidDecision,
  useRequestFinancialAidMoreInfo,
  useUpdateFinancialAidApplication,
} from '../../../financial-aid/hooks/useFinancialAidApi';
import { asArray, asRecord } from '../utils/domainData';
import {
  buildFinancialAidCorrectionPatch,
  filterFinancialAidCorrectionFields,
  flattenFinancialAidCorrectionFields,
  formatFinancialAidCorrectionFieldOptionLabel,
  listFinancialAidCorrectionDraftPaths,
  mergeFinancialAidCorrectionSections,
  pruneFinancialAidCorrectionDraftSections,
  readFinancialAidCorrectionInputValue,
  readFinancialAidCorrectionPathValue,
  readCorrectionExpectedUpdatedAt,
  removeFinancialAidCorrectionDraftPath,
  resolveFinancialAidCorrectionFieldSelection,
} from './financialAidCorrectionState';
import { isFinancialAidAssignedStaffUserId } from '@triedge/financial-aid-domain';
import {
  publishFinancialAidReportStaleSignal,
  readFinancialAidReportStaleSignal,
  subscribeFinancialAidReportStaleSignal,
  type FinancialAidReportStaleSignal,
} from '../reportStaleSignals';

const readText = (value: unknown, defaultValue = ''): string => {
  const next = String(value ?? '').trim();
  return next || defaultValue;
};

const isStaffUserId = (value: unknown): value is string => {
  const normalized = readText(value);
  return isFinancialAidAssignedStaffUserId(normalized);
};

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeFinancialAidTimestamp = (value: unknown): number | null => {
  const numeric = asNumber(value);
  if (numeric == null) {
    return null;
  }
  return numeric > 10_000_000_000 ? Math.floor(numeric / 1000) : numeric;
};

const toEpochMs = (value: unknown): number | null => {
  const numeric = asNumber(value);
  if (numeric != null) {
    return numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
  }
  const raw = readText(value);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDate = (value: unknown): string => {
  const ms = toEpochMs(value);
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleDateString('en-US');
  } catch {
    return '';
  }
};

const normalizeStatusToken = (value: unknown): string => {
  const raw = readText(value).toUpperCase().replace(/[\s-]+/g, '_');
  if (!raw) return 'NEW';
  if (raw === 'DECLINED') return 'DENIED';
  if (raw === 'UNDER_REVIEW') return 'IN_PROGRESS';
  return raw;
};

const toStatusLabel = (token: string): string => token.replace(/_/g, ' ');
const canRequestMoreInfoFromStatus = (token: string): boolean =>
  token === 'SUBMITTED' || token === 'IN_PROGRESS' || token === 'UNDER_REVIEW';
const isRequestMoreInfoTransitionError = (message: string): boolean =>
  /invalid transition|cannot transition/i.test(message);
const readRequestMoreInfoTransitionStatus = (message: string): string => {
  const match = message.match(/(?:from|Status)\s+'([^']+)'/i);
  return match ? normalizeStatusToken(match[1]) : '';
};
const buildRequestMoreInfoUnavailableMessage = (statusToken: string): string => {
  const statusLabel = toStatusLabel(statusToken || 'NEW').toLowerCase();
  return `Request More Info is only available for submitted or under-review applications. This application is currently ${statusLabel}.`;
};

const readLatestStaffCorrectionTimestamp = (sectionsValue: unknown): number | null => {
  const additionalInfo = asRecord(asRecord(sectionsValue).additionalInfo);
  return asArray(asRecord(additionalInfo).staffCorrections).reduce<number | null>((latest, entry) => {
    const correctedAt = normalizeFinancialAidTimestamp(asRecord(entry).correctedAt);
    if (correctedAt == null) {
      return latest;
    }
    return latest == null ? correctedAt : Math.max(latest, correctedAt);
  }, null);
};

const statusConfig: Record<string, { bg: string; text: string; dot: string }> = {
  APPLIED: { bg: '#ECFDF5', text: '#237062', dot: '#28E2A7' },
  IN_PROGRESS: { bg: '#ECFDF5', text: '#237062', dot: '#2697A2' },
  SCHEDULED: { bg: '#FEE2E2', text: '#FE2C3B', dot: '#FF5F5F' },
  DECISION: { bg: '#ECFDF5', text: '#237062', dot: '#92EEBA' },
  APPROVED: { bg: '#ECFDF5', text: '#237062', dot: '#92EEBA' },
  ACCEPTED: { bg: '#ECFDF5', text: '#237062', dot: '#28E2A7' },
  DENIED: { bg: '#FEE2E2', text: '#FE2C3B', dot: '#FE2C3B' },
  NEW: { bg: '#ECFDF5', text: '#237062', dot: '#28E2A7' },
};

const DETAIL_COMPACT_TRIGGER_WIDTH_PX = 768;
const DETAIL_HEADER_STACK_WIDTH_PX = 1280;
type DetailLayoutMode = 'compact' | 'full';
type DecisionEmailKind = 'approve' | 'deny';
type DecisionSubmitOptions = {
  sendExternalParentEmail?: boolean;
};

type DecisionEmailPreview = {
  kind: DecisionEmailKind;
  to: string;
  eventType: string;
  statusLabel: string;
  actionHeading: string;
  actionLabel: string;
  subject: string;
  message: string;
  actionBody: string;
};

const buildExternalParentEmailApprovalPayload = (sendExternalParentEmail: boolean) => (
  sendExternalParentEmail
    ? {
      sendExternalParentEmail: true,
      externalParentEmailApproved: true,
    }
    : {}
);

const Toast = ({
  message,
  tone,
  onClose,
}: {
  message: string;
  tone: 'success' | 'error';
  onClose: () => void;
}) => (
  <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[200] animate-in slide-in-from-top-4 duration-300">
    <div
      className={`flex items-center gap-3 px-6 py-3 rounded-2xl shadow-2xl ${
        tone === 'success' ? 'bg-primary text-white' : 'bg-strawberry text-white'
      }`}
    >
      {tone === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
      <p className="text-sm font-bold">{message}</p>
      <button type="button" onClick={onClose} className="ml-2 p-1 hover:bg-white/10 rounded-lg transition-colors">
        <X size={16} />
      </button>
    </div>
  </div>
);

export type FinancialAidStaffDetailContext = {
  applicationId: string;
  reportStaleAfter: number | null;
  review: FinancialAidReviewDetail | null;
  statusHistory: FinancialAidStatusHistoryResponse | null;
  layoutMode: DetailLayoutMode;
  refetch: () => Promise<void>;
  isCorrectionEditing: boolean;
  correctionReason: string;
  draftSections: Record<string, unknown>;
  setCorrectionReason: (value: string) => void;
  updateCorrectionSection: (sectionKey: string, patch: unknown) => void;
  beginCorrectionEditing: () => void;
  correctionEditor: ReactNode;
};

type SidebarTab = {
  id: string;
  icon: LucideIcon;
  label: string;
  badge?: number;
};

export function ApplicantDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId: rawId } = useParams<{ applicationId: string }>();
  const applicationId = readText(rawId);
  const detailRootRef = useRef<HTMLElement | null>(null);
  const assignMenuRef = useRef<HTMLDivElement | null>(null);

  const detail = useGetFinancialAidApplicationForReviewQuery(applicationId, { skip: !applicationId });
  const history = useGetFinancialAidStatusHistoryQuery(applicationId, { skip: !applicationId });
  const makeDecision = useMakeFinancialAidDecision();
  const assignMutation = useAssignFinancialAidApplication();
  const requestMoreInfoMutation = useRequestFinancialAidMoreInfo();
  const updateApplication = useUpdateFinancialAidApplication();
  const settingsConfig = useGetSettingsModuleConfigQuery({ moduleKey: 'staff_settings' });

  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isAssignMenuOpen, setIsAssignMenuOpen] = useState(false);
  const [optimisticAssigneeName, setOptimisticAssigneeName] = useState<string | null>(null);
  const [isMoreInfoMode, setIsMoreInfoMode] = useState(false);
  const [moreInfoMessage, setMoreInfoMessage] = useState('');
  const [moreInfoEmailConfirmed, setMoreInfoEmailConfirmed] = useState(false);
  const [moreInfoErrorMessage, setMoreInfoErrorMessage] = useState('');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isCorrectionEditing, setIsCorrectionEditing] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [draftSections, setDraftSections] = useState<Record<string, unknown>>({});
  const [correctionFieldSearch, setCorrectionFieldSearch] = useState('');
  const [correctionFieldPath, setCorrectionFieldPath] = useState('');
  const [correctionFieldValue, setCorrectionFieldValue] = useState('');
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [busyAction, setBusyAction] = useState<'approve' | 'deny' | 'more-info' | null>(null);
  const [optimisticStatusToken, setOptimisticStatusToken] = useState<string | null>(null);
  const [optimisticReportStaleAfter, setOptimisticReportStaleAfter] = useState<number | null>(null);
  const [decisionEmailPreviewKind, setDecisionEmailPreviewKind] = useState<DecisionEmailKind | null>(null);
  const [decisionEmailConfirmed, setDecisionEmailConfirmed] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [detailSurfaceWidth, setDetailSurfaceWidth] = useState(0);

  const review = detail.data ?? null;
  const statusHistory = history.data ?? null;
  const reviewRecord = asRecord(review as unknown);
  const applicationRecord = asRecord(reviewRecord.application);
  const sections = asRecord(applicationRecord.sections);
  const additionalInfo = asRecord(sections.additionalInfo);
  const familyInformation = asRecord(sections.familyInformation);
  const parent1 = asRecord(sections.parent1);
  const parent2 = asRecord(sections.parent2);
  const recommendation = asRecord(reviewRecord.recommendation);
  const contractFlags = asRecord(sections.contractFlags);

  const familyName = readText(
    familyInformation.familyName,
    readText(applicationRecord.familyName, readText(applicationRecord.code, 'Family'))
  );
  const code = readText(applicationRecord.code, applicationId);
  const statusToken = normalizeStatusToken(applicationRecord.status);
  const displayStatusToken = optimisticStatusToken ?? statusToken;
  const submittedDate = formatDate(
    applicationRecord.submittedAt ?? applicationRecord.createdAt ?? applicationRecord.updatedAt
  );
  const persistedAssigneeName = readText(
    applicationRecord.assigneeName,
    readText(asRecord(applicationRecord.assignee).name, '')
  );
  const assigneeName = optimisticAssigneeName !== null ? optimisticAssigneeName : persistedAssigneeName;
  const persistedReportStaleAfter = useMemo(
    () => readLatestStaffCorrectionTimestamp(sections),
    [sections],
  );
  const reportStaleAfter = optimisticReportStaleAfter == null
    ? persistedReportStaleAfter
    : Math.max(persistedReportStaleAfter ?? optimisticReportStaleAfter, optimisticReportStaleAfter);

  useEffect(() => {
    setOptimisticAssigneeName(null);
  }, [persistedAssigneeName]);

  useEffect(() => {
    setOptimisticReportStaleAfter(null);
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId) {
      return undefined;
    }
    const applyStaleSignal = (signal: FinancialAidReportStaleSignal) => {
      setOptimisticReportStaleAfter((current) => Math.max(current ?? signal.staleAfter, signal.staleAfter));
    };
    const storedSignal = readFinancialAidReportStaleSignal(applicationId);
    if (storedSignal) {
      applyStaleSignal(storedSignal);
    }
    return subscribeFinancialAidReportStaleSignal(applicationId, applyStaleSignal);
  }, [applicationId]);

  useEffect(() => {
    if (
      optimisticReportStaleAfter != null
      && persistedReportStaleAfter != null
      && persistedReportStaleAfter >= optimisticReportStaleAfter
    ) {
      setOptimisticReportStaleAfter(null);
    }
  }, [optimisticReportStaleAfter, persistedReportStaleAfter]);

  const staffAssignOptions = useMemo(() => {
    const payload = asRecord(asRecord(settingsConfig.data).payload);
    const staffRows = Array.isArray(payload.staff) ? payload.staff : [];
    const options: Array<{ id: string | null; name: string }> = [];
    staffRows.forEach((entry) => {
      const record = asRecord(entry);
      const name = readText(
        record.displayName,
        readText(record.name, `${readText(record.firstName)} ${readText(record.lastName)}`.trim()),
      );
      if (!name) return;
      const rawId =
        readText(record.id) ||
        readText(record.userId) ||
        readText(record.staffId);
      const id = isStaffUserId(rawId) ? rawId : null;
      options.push({ id, name });
    });
    return options;
  }, [settingsConfig.data]);
  const recommendedPercent =
    asNumber(recommendation.recommendedPercent)
    ?? asNumber(asRecord(applicationRecord.recommendation).recommendedPercent)
    ?? asNumber(asRecord(applicationRecord.finalAward).scholarshipPercent)
    ?? 0;

  const primaryContactName = readText(
    `${readText(parent1.firstName)} ${readText(parent1.lastName)}`,
    readText(`${readText(parent2.firstName)} ${readText(parent2.lastName)}`, 'Not provided')
  );
  const primaryEmail = readText(parent1.email, readText(parent2.email, 'No email on file'));
  const primaryPhone = readText(
    parent1.cellPhone,
    readText(parent1.workPhone, readText(parent2.cellPhone, readText(parent2.workPhone, 'No phone on file')))
  );
  const parent1Email = readText(parent1.email);
  const parent2Email = readText(parent2.email);
  const decisionEmailRecipient = parent1Email || parent2Email;

  const flagCount = useMemo(() => {
    const contractFlagItems = asArray<Record<string, unknown>>(contractFlags.flags).length;
    const recommendationFlagItems = asArray<Record<string, unknown>>(recommendation.flags).length;
    return contractFlagItems || recommendationFlagItems;
  }, [contractFlags.flags, recommendation.flags]);

  const sidebarTabs = useMemo<SidebarTab[]>(
    () => [
      { id: 'family-profile', icon: Users, label: 'Family Profile' },
      { id: 'income-calculation', icon: Building2, label: 'Income & Calculation' },
      { id: 'assets-equity', icon: Home, label: 'Assets & Equity' },
      { id: 'expenses-spending', icon: Receipt, label: 'Expenses & Spending' },
      { id: 'contract-flags', icon: FileCheck, label: 'Contract & Flags', badge: flagCount || undefined },
      { id: 'corrections', icon: Pencil, label: 'Corrections' },
      { id: 'documents', icon: FolderOpen, label: 'Documents' },
      { id: 'notes', icon: MessageSquare, label: 'Notes' },
    ],
    [flagCount]
  );
  const activePathId = location.pathname.split('/').filter(Boolean).pop();
  const activeTab = sidebarTabs.find((tab) => tab.id === activePathId) ?? sidebarTabs[0];
  const isCorrectionsTabActive = activePathId === 'corrections';
  const sectionNavItems = useMemo<ModuleSectionNavItem[]>(
    () => sidebarTabs.map((tab) => ({
      id: tab.id,
      label: tab.label,
      to: tab.id,
      icon: tab.icon,
      badge: tab.badge,
    })),
    [sidebarTabs],
  );
  const isCompactLayout = detailSurfaceWidth > 0 && detailSurfaceWidth <= DETAIL_COMPACT_TRIGGER_WIDTH_PX;
  const shouldStackDetailHeader =
    isCompactLayout || (detailSurfaceWidth > 0 && detailSurfaceWidth <= DETAIL_HEADER_STACK_WIDTH_PX);
  const layoutMode: DetailLayoutMode = isCompactLayout ? 'compact' : 'full';
  const rootLayoutClass = isCompactLayout ? 'flex-col' : 'flex-row';
  const rootWidthClass = isCompactLayout ? 'w-full max-w-[760px] mx-auto' : 'w-full';
  const mobileHeaderClass = isCompactLayout ? 'flex' : 'hidden';
  const sidebarClass = isCompactLayout ? 'hidden' : 'flex';
  const headerPaddingClass = isCompactLayout ? 'px-6 py-4' : 'px-10 py-6';
  const headerContentClass = shouldStackDetailHeader ? 'flex-col' : 'flex-row items-center';
  const titleClass = isCompactLayout ? 'text-xl' : shouldStackDetailHeader ? 'text-3xl leading-tight' : 'text-4xl leading-tight';
  const metaClass = isCompactLayout ? 'text-xs' : 'text-sm';
  const actionRowClass = shouldStackDetailHeader ? 'gap-2 pb-1' : 'gap-2 pb-0 justify-start md:justify-end';
  const actionButtonClass = isCompactLayout ? 'px-3 py-2 text-xs' : 'px-4 py-2 text-[90%]';
  const mainPaddingClass = isCompactLayout ? 'p-4' : 'p-10';
  const titleStatusRowClass = shouldStackDetailHeader
    ? 'flex-col items-start gap-2'
    : 'items-center gap-3 flex-wrap';
  const titleWidthClass = shouldStackDetailHeader
    ? 'w-full'
    : 'min-w-0 max-w-full';
  const effectiveCorrectionSections = useMemo(
    () => asRecord(mergeFinancialAidCorrectionSections(sections, draftSections)),
    [draftSections, sections],
  );
  const correctionFields = useMemo(
    () => flattenFinancialAidCorrectionFields(sections, {
      academicYear: applicationRecord.academicYear,
    }),
    [applicationRecord.academicYear, sections],
  );
  const correctionFieldByPath = useMemo(
    () => new Map(correctionFields.map((field) => [field.path, field])),
    [correctionFields],
  );
  const visibleCorrectionFields = useMemo(
    () => filterFinancialAidCorrectionFields(correctionFields, correctionFieldSearch),
    [correctionFieldSearch, correctionFields],
  );
  const stagedCorrectionPaths = useMemo(
    () => listFinancialAidCorrectionDraftPaths(sections, draftSections),
    [draftSections, sections],
  );
  const stagedCorrectionPathSet = useMemo(() => new Set(stagedCorrectionPaths), [stagedCorrectionPaths]);
  const groupedCorrectionFields = useMemo(() => {
    const groups = new Map<string, typeof correctionFields>();
    visibleCorrectionFields.forEach((field) => {
      const group = field.group || 'Submitted fields';
      groups.set(group, [...(groups.get(group) ?? []), field]);
    });
    return Array.from(groups.entries());
  }, [visibleCorrectionFields]);
  const stagedCorrectionSummaries = useMemo(() =>
    stagedCorrectionPaths.map((path) => {
      const field = correctionFieldByPath.get(path);
      return {
        path,
        group: field?.group ?? 'Submitted fields',
        label: field?.label ?? path,
        beforeValue: readFinancialAidCorrectionInputValue(readFinancialAidCorrectionPathValue(sections, path)),
        afterValue: readFinancialAidCorrectionInputValue(readFinancialAidCorrectionPathValue(effectiveCorrectionSections, path)),
      };
    }),
  [correctionFieldByPath, effectiveCorrectionSections, sections, stagedCorrectionPaths]);
  const selectedCorrectionField = correctionFields.find((field) => field.path === correctionFieldPath) ?? null;
  const hasCorrectionDraft = stagedCorrectionPaths.length > 0;
  const correctionReasonReady = correctionReason.trim().length > 0;
  const correctionSaveDisabled = updateApplication.isPending || !hasCorrectionDraft || !correctionReasonReady;

  const refetch = useCallback(async () => {
    await Promise.all([
      detail.refetch(),
      history.refetch(),
    ]);
  }, [detail.refetch, history.refetch]);

  const updateCorrectionSection = useCallback((sectionKey: string, patch: unknown) => {
    setDraftSections((current) => pruneFinancialAidCorrectionDraftSections(sections, {
      ...current,
      [sectionKey]: mergeFinancialAidCorrectionSections(current[sectionKey], patch),
    }));
  }, [sections]);

  const readCorrectionInputForPath = (path: string, fallback = ''): string => {
    if (!path) {
      return '';
    }
    if (stagedCorrectionPathSet.has(path)) {
      return readFinancialAidCorrectionInputValue(
        readFinancialAidCorrectionPathValue(effectiveCorrectionSections, path)
      );
    }
    return correctionFields.find((field) => field.path === path)?.inputValue ?? fallback;
  };

  const setSelectedCorrectionPath = (path: string) => {
    setCorrectionFieldPath(path);
    setCorrectionFieldValue(readCorrectionInputForPath(path));
  };

  useEffect(() => {
    if (!isCorrectionEditing) {
      return;
    }
    const nextSelection = resolveFinancialAidCorrectionFieldSelection(
      correctionFields,
      visibleCorrectionFields,
      correctionFieldPath,
    );
    const nextPath = nextSelection?.path ?? '';
    if (nextPath !== correctionFieldPath) {
      setCorrectionFieldPath(nextPath);
      setCorrectionFieldValue(readCorrectionInputForPath(nextPath, nextSelection?.inputValue ?? ''));
    }
  }, [
    correctionFieldPath,
    correctionFields,
    effectiveCorrectionSections,
    isCorrectionEditing,
    stagedCorrectionPathSet,
    visibleCorrectionFields,
  ]);

  const beginCorrectionEditing = useCallback(() => {
    const firstField = correctionFields[0] ?? null;
    if (!correctionFieldPath && firstField) {
      setCorrectionFieldPath(firstField.path);
      setCorrectionFieldValue(firstField.inputValue);
    }
    setIsCorrectionEditing(true);
  }, [correctionFieldPath, correctionFields]);

  const effectiveSelectedCorrectionInputValue = useMemo(() => (
    correctionFieldPath
      ? readFinancialAidCorrectionInputValue(
          readFinancialAidCorrectionPathValue(effectiveCorrectionSections, correctionFieldPath)
        )
      : ''
  ), [correctionFieldPath, effectiveCorrectionSections]);

  const canApplyCorrection = useMemo(() => {
    if (!selectedCorrectionField || !correctionFieldPath) {
      return false;
    }
    if (correctionFieldValue === effectiveSelectedCorrectionInputValue) {
      return false;
    }
    try {
      buildFinancialAidCorrectionPatch(
        effectiveCorrectionSections,
        correctionFieldPath,
        correctionFieldValue,
      );
      return true;
    } catch {
      return false;
    }
  }, [
    correctionFieldPath,
    correctionFieldValue,
    effectiveCorrectionSections,
    effectiveSelectedCorrectionInputValue,
    selectedCorrectionField,
  ]);

  const applySelectedCorrectionField = () => {
    if (!canApplyCorrection) {
      return;
    }
    try {
      const { sectionKey, patch } = buildFinancialAidCorrectionPatch(
        effectiveCorrectionSections,
        correctionFieldPath,
        correctionFieldValue,
      );
      updateCorrectionSection(sectionKey, patch);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to apply this correction.';
      setToast({ tone: 'error', message });
    }
  };

  const resetCorrectionFieldInputFromSections = (path: string) => {
    setCorrectionFieldValue(
      readFinancialAidCorrectionInputValue(readFinancialAidCorrectionPathValue(sections, path))
    );
  };

  const clearCorrectionDraftFields = () => {
    setDraftSections({});
    if (correctionFieldPath) {
      resetCorrectionFieldInputFromSections(correctionFieldPath);
    }
  };

  const unstageCorrectionPath = (path: string) => {
    setDraftSections((current) => removeFinancialAidCorrectionDraftPath(sections, current, path));
    if (path === correctionFieldPath) {
      resetCorrectionFieldInputFromSections(path);
    }
  };

  const resetCorrectionDraft = () => {
    setIsCorrectionEditing(false);
    setCorrectionReason('');
    setDraftSections({});
    setCorrectionFieldSearch('');
    setCorrectionFieldPath('');
    setCorrectionFieldValue('');
  };

  const handleSaveCorrection = async () => {
    if (!applicationId || correctionSaveDisabled) {
      return;
    }
    const expectedUpdatedAt = readCorrectionExpectedUpdatedAt(applicationRecord.updatedAt);
    if (expectedUpdatedAt == null) {
      setToast({ tone: 'error', message: 'Application version is unavailable. Refresh before saving corrections.' });
      return;
    }
    try {
      const updatedApplication = await updateApplication.mutateAsync({
        id: applicationId,
        sections: draftSections,
        correctionMode: 'staff_correction',
        correctionReason: correctionReason.trim(),
        expectedUpdatedAt,
      });
      const updatedRecord = asRecord(updatedApplication);
      const updatedSections = asRecord(updatedRecord.sections);
      const nextReportStaleAfter =
        readLatestStaffCorrectionTimestamp(updatedSections)
          ?? normalizeFinancialAidTimestamp(updatedRecord.updatedAt)
          ?? Math.floor(Date.now() / 1000);
      setOptimisticReportStaleAfter(nextReportStaleAfter);
      publishFinancialAidReportStaleSignal({
        applicationId,
        staleAfter: nextReportStaleAfter,
      });
      setToast({ tone: 'success', message: 'Application corrections saved.' });
      resetCorrectionDraft();
      await refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save corrections right now.';
      setToast({ tone: 'error', message });
    }
  };

  const correctionEditor = isCorrectionEditing ? (
    <div className="mt-4 space-y-4 rounded-xl border border-[#E5E7EB] bg-white p-4">
      <label
        className="block text-[10px] font-bold uppercase tracking-widest text-[#6B7280]"
        htmlFor="financial-aid-correction-reason"
        title="Required audit reason saved with this staff correction."
      >
        Reason
        <input
          id="financial-aid-correction-reason"
          type="text"
          value={correctionReason}
          onChange={(event) => setCorrectionReason(event.currentTarget.value)}
          placeholder="Reason for staff correction"
          title="Required audit reason saved with this staff correction."
          className="mt-2 w-full rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-[#111827] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </label>
      {correctionFields.length === 0 ? (
        <div
          data-testid="financial-aid-correction-empty-state"
          className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm font-medium text-[#6B7280]"
        >
          Parent-submitted financial aid snapshot has not been captured for this application.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label
              className="block min-w-[220px] flex-1 text-[10px] font-bold uppercase tracking-widest text-[#6B7280]"
              htmlFor="financial-aid-correction-search"
              title="Search by field name, section name, row number, or stored field path."
            >
              Search
              <input
                id="financial-aid-correction-search"
                type="search"
                value={correctionFieldSearch}
                onChange={(event) => setCorrectionFieldSearch(event.currentTarget.value)}
                placeholder="Find a submitted field"
                title="Search by field name, section name, row number, or stored field path."
                className="mt-2 w-full rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-[#111827] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
            {stagedCorrectionPaths.length > 0 ? (
              <div className="flex items-center gap-2">
                <div
                  data-testid="financial-aid-correction-staged-count"
                  className="rounded-lg bg-primary/5 px-3 py-2 text-xs font-semibold text-primary"
                >
                  {stagedCorrectionPaths.length} staged
                </div>
                <button
                  type="button"
                  onClick={clearCorrectionDraftFields}
                  className="inline-flex h-[34px] items-center gap-1 rounded-lg border border-[#D1D5DB] px-3 text-xs font-semibold text-[#374151] transition-colors hover:border-primary hover:text-primary"
                  title="Clear all staged correction values before saving."
                >
                  <X size={14} />
                  Clear
                </button>
              </div>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
            <label
              className="block text-[10px] font-bold uppercase tracking-widest text-[#6B7280]"
              htmlFor="financial-aid-correction-field"
              title="Every option is a field the parent submitted in the Financial Aid form snapshot."
            >
              Field
              <select
                id="financial-aid-correction-field"
                value={correctionFieldPath}
                onChange={(event) => setSelectedCorrectionPath(event.currentTarget.value)}
                title="Every option is a field the parent submitted in the Financial Aid form snapshot."
                className="mt-2 w-full rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-[#111827] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
              >
                {groupedCorrectionFields.map(([group, fields]) => (
                  <optgroup key={group} label={group}>
                    {fields.map((field) => (
                      <option key={field.path} value={field.path}>
                        {formatFinancialAidCorrectionFieldOptionLabel(field)}
                        {stagedCorrectionPathSet.has(field.path) ? ' (staged)' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <span className="mt-1 block text-[11px] font-medium normal-case tracking-normal text-[#6B7280]">
                {visibleCorrectionFields.length} shown
              </span>
            </label>
            <label
              className="block text-[10px] font-bold uppercase tracking-widest text-[#6B7280]"
              htmlFor="financial-aid-correction-current-value"
              title="The current submitted value saved on the application."
            >
              Current
              <input
                id="financial-aid-correction-current-value"
                type="text"
                value={selectedCorrectionField?.inputValue ?? ''}
                readOnly
                title="The current submitted value saved on the application."
                className="mt-2 w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-sm font-medium normal-case tracking-normal text-[#6B7280]"
              />
            </label>
            <label
              className="block text-[10px] font-bold uppercase tracking-widest text-[#6B7280]"
              htmlFor="financial-aid-correction-field-value"
              title="Enter the replacement value that should appear on the staff and parent application report."
            >
              Corrected Value
              {selectedCorrectionField?.type === 'boolean' ? (
                <select
                  id="financial-aid-correction-field-value"
                  value={correctionFieldValue}
                  onChange={(event) => setCorrectionFieldValue(event.currentTarget.value)}
                  title="Enter the replacement value that should appear on the staff and parent application report."
                  className="mt-2 w-full rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-[#111827] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Choose</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <input
                  id="financial-aid-correction-field-value"
                  type={selectedCorrectionField?.type === 'number' ? 'number' : 'text'}
                  value={correctionFieldValue}
                  onChange={(event) => setCorrectionFieldValue(event.currentTarget.value)}
                  title="Enter the replacement value that should appear on the staff and parent application report."
                  className="mt-2 w-full rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-[#111827] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              )}
            </label>
            <button
              type="button"
              onClick={applySelectedCorrectionField}
              disabled={!canApplyCorrection}
              className="inline-flex h-[38px] items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
              title="Stage this field value before saving the correction set."
            >
              Apply
            </button>
          </div>
          {selectedCorrectionField ? (
            <div
              data-testid="financial-aid-correction-selected-context"
              className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-xs text-[#374151]"
              title={selectedCorrectionField.path}
            >
              <span className="font-semibold text-[#111827]">
                {formatFinancialAidCorrectionFieldOptionLabel(selectedCorrectionField)}
              </span>
              <span className="ml-2 break-all text-[#6B7280]">{selectedCorrectionField.path}</span>
            </div>
          ) : null}
          {selectedCorrectionField && stagedCorrectionPathSet.has(selectedCorrectionField.path) ? (
            <p
              data-testid="financial-aid-correction-selected-staged"
              className="text-xs font-semibold text-primary"
            >
              Applied to draft
            </p>
          ) : null}
          {isCorrectionsTabActive ? (
            <div
              data-testid="financial-aid-correction-field-list"
              className="max-h-[420px] overflow-y-auto rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-2"
            >
              {visibleCorrectionFields.map((field) => {
                const isSelected = field.path === correctionFieldPath;
                return (
                  <button
                    key={field.path}
                    type="button"
                    onClick={() => setSelectedCorrectionPath(field.path)}
                    data-testid="financial-aid-correction-field-list-row"
                    className={`grid w-full grid-cols-1 gap-1 rounded-md px-3 py-2 text-left text-xs transition-colors sm:grid-cols-[minmax(0,1fr)_minmax(8rem,0.45fr)] ${
                      isSelected ? 'bg-white text-[#111827] shadow-sm ring-1 ring-primary/20' : 'text-[#374151] hover:bg-white'
                    }`}
                    title={field.path}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold">
                        {formatFinancialAidCorrectionFieldOptionLabel(field)}
                        {stagedCorrectionPathSet.has(field.path) ? ' (staged)' : ''}
                      </span>
                      <span className="block truncate text-[11px] text-[#6B7280]">{field.path}</span>
                    </span>
                    <span className="truncate font-medium text-[#6B7280] sm:text-right">
                      {field.inputValue || 'Blank'}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
          {stagedCorrectionSummaries.length > 0 ? (
            <div className="space-y-2 border-t border-[#E5E7EB] pt-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">
                Staged Corrections
              </div>
              <div className="space-y-2">
                {stagedCorrectionSummaries.map((summary) => (
                  <div
                    key={summary.path}
                    className="grid gap-2 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-xs text-[#374151] lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto] lg:items-center"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-[#111827]">
                        {summary.group} - {summary.label}
                      </div>
                      <div className="truncate text-[11px] text-[#6B7280]">{summary.path}</div>
                    </div>
                    <div className="grid min-w-0 gap-1 sm:grid-cols-2">
                      <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">Before</div>
                        <div className="truncate font-medium text-[#111827]">{summary.beforeValue || 'Blank'}</div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">After</div>
                        <div className="truncate font-medium text-primary">{summary.afterValue || 'Blank'}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => unstageCorrectionPath(summary.path)}
                      className="inline-flex h-[32px] items-center justify-center rounded-lg border border-[#D1D5DB] px-3 text-xs font-semibold text-[#374151] transition-colors hover:border-primary hover:text-primary"
                      title="Remove this field from the correction draft."
                    >
                      Unstage
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  ) : null;

  const ctx: FinancialAidStaffDetailContext = useMemo(
    () => ({
      applicationId,
      reportStaleAfter,
      review,
      statusHistory,
      layoutMode,
      refetch,
      isCorrectionEditing,
      correctionReason,
      draftSections,
      setCorrectionReason,
      updateCorrectionSection,
      beginCorrectionEditing,
      correctionEditor,
    }),
    [
      applicationId,
      beginCorrectionEditing,
      correctionEditor,
      correctionReason,
      draftSections,
      isCorrectionEditing,
      layoutMode,
      refetch,
      reportStaleAfter,
      review,
      statusHistory,
      updateCorrectionSection,
    ],
  );

  const decisionEmailPreview = useMemo<DecisionEmailPreview | null>(() => {
    if (!decisionEmailPreviewKind) return null;
    const isApproval = decisionEmailPreviewKind === 'approve';
    const eventType = isApproval ? 'Award Decision Ready' : 'Decision Notice';
    const statusLabel = isApproval ? 'Approved' : 'Decision Posted';
    const actionHeading = isApproval ? 'Award available' : 'Decision posted';
    const actionLabel = isApproval ? 'Review award' : 'Review decision';
    const applicationCode = code || applicationId || 'Application';
    return {
      kind: decisionEmailPreviewKind,
      to: decisionEmailRecipient || 'No parent email on file',
      eventType,
      statusLabel,
      actionHeading,
      actionLabel,
      subject: `${eventType} — ${familyName} (${applicationCode})`,
      message: isApproval
        ? `A financial aid decision is ready for the ${familyName} family application (${applicationCode}). Please review it in the parent portal.`
        : `A financial aid decision is ready for the ${familyName} family application (${applicationCode}). Please review the decision letter and next steps in the parent portal.`,
      actionBody: isApproval
        ? 'Your financial aid decision is ready for review in the portal.'
        : 'A financial aid decision has been posted to your portal.',
    };
  }, [applicationId, code, decisionEmailPreviewKind, decisionEmailRecipient, familyName]);

  const moreInfoEmailPreview = useMemo(() => {
    const applicationCode = code || applicationId || 'Application';
    const requestedInfo = moreInfoMessage.trim()
      || 'Describe the documents or details the family should provide.';
    return {
      to: decisionEmailRecipient || 'No parent email on file',
      subject: `Additional Information Required — ${familyName} (${applicationCode})`,
      message: [
        `The financial aid office needs a little more information to continue reviewing the ${familyName} family application (${applicationCode}).`,
        `Requested information: ${requestedInfo}`,
        'Please use the parent portal to review the request and upload any documents.',
      ].join('\n\n'),
    };
  }, [applicationId, code, decisionEmailRecipient, familyName, moreInfoMessage]);

  const closeCompleteModal = () => {
    setIsCompleteModalOpen(false);
    setDecisionEmailPreviewKind(null);
    setDecisionEmailConfirmed(false);
    setMoreInfoErrorMessage('');
  };

  const showMoreInfoError = (message: string) => {
    setMoreInfoErrorMessage(message);
  };

  const handleAssignTo = async (option: { id: string | null; name: string } | null) => {
    if (!applicationId) return;
    setIsAssignMenuOpen(false);
    if (option && !option.id) {
      setToast({ tone: 'error', message: 'This staff record does not include a valid user ID yet.' });
      return;
    }
    const previous = assigneeName;
    setOptimisticAssigneeName(option?.name ?? '');
    try {
      await assignMutation.mutateAsync({
        applicationId,
        assignedTo: option ? option.id : null,
        assigneeName: option?.name ?? null,
      });
      setToast({ tone: 'success', message: option ? `Assigned to ${option.name}.` : 'Assignment cleared.' });
      void refetch();
    } catch (error) {
      setOptimisticAssigneeName(previous || null);
      const message = error instanceof Error ? error.message : 'Unable to update the assignment right now.';
      setToast({ tone: 'error', message });
    }
  };

  // INT-819: keep the assign menu keyboard navigable, mirroring the dashboard
  // actions menu (Escape closes; Arrow/Home/End move focus and keep the
  // active option scrolled into view inside the scrollable list).
  const handleAssignMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setIsAssignMenuOpen(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      return;
    }
    const container = assignMenuRef.current;
    if (!container) return;
    const menuItems = Array.from(
      container.querySelectorAll<HTMLButtonElement>('button:not([disabled])'),
    );
    if (menuItems.length === 0) return;
    event.preventDefault();
    const activeIndex = menuItems.findIndex((item) => item === document.activeElement);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? menuItems.length - 1
          : event.key === 'ArrowDown'
            ? (activeIndex + 1) % menuItems.length
            : activeIndex <= 0
              ? menuItems.length - 1
              : activeIndex - 1;
    const nextItem = menuItems[nextIndex];
    if (!nextItem) return;
    nextItem.focus({ preventScroll: true });
    nextItem.scrollIntoView?.({ block: 'nearest' });
  };

  const handleRequestMoreInfo = async (options: DecisionSubmitOptions = {}) => {
    if (!applicationId) return;
    const message = moreInfoMessage.trim();
    if (!message) {
      showMoreInfoError('Add a short note describing what the family should provide.');
      return;
    }
    if (options.sendExternalParentEmail && !moreInfoEmailConfirmed) {
      showMoreInfoError('Please confirm the email preview before queueing a parent email.');
      return;
    }
    if (options.sendExternalParentEmail && !decisionEmailRecipient) {
      showMoreInfoError('Add a parent email before queueing this message.');
      return;
    }
    if (!canRequestMoreInfoFromStatus(displayStatusToken)) {
      showMoreInfoError(buildRequestMoreInfoUnavailableMessage(displayStatusToken));
      return;
    }
    setMoreInfoErrorMessage('');
    setBusyAction('more-info');
    try {
      await requestMoreInfoMutation.mutateAsync({
        applicationId,
        message,
        ...buildExternalParentEmailApprovalPayload(Boolean(options.sendExternalParentEmail)),
      });
      setOptimisticStatusToken(normalizeStatusToken('more_info_needed'));
      setToast({
        tone: 'success',
        message: options.sendExternalParentEmail
          ? `${familyName} moved to More Info Needed and parent email queued.`
          : `${familyName} moved to More Info Needed. Parent email was not sent.`,
      });
      setIsMoreInfoMode(false);
      setMoreInfoMessage('');
      setMoreInfoEmailConfirmed(false);
      closeCompleteModal();
      void refetch();
    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : '';
      const transitionStatusToken = readRequestMoreInfoTransitionStatus(rawMessage);
      const fallback = isRequestMoreInfoTransitionError(rawMessage)
        ? buildRequestMoreInfoUnavailableMessage(transitionStatusToken || displayStatusToken)
        : (rawMessage || 'Unable to request more information right now.');
      showMoreInfoError(fallback);
    } finally {
      setBusyAction(null);
    }
  };

  const openDecisionEmailPreview = (kind: DecisionEmailKind) => {
    setDecisionEmailPreviewKind(kind);
    setDecisionEmailConfirmed(false);
  };

  const handleApprove = async (options: DecisionSubmitOptions = {}) => {
    if (!applicationId) return;
    setBusyAction('approve');
    try {
      const result = await makeDecision.mutateAsync({
        applicationId,
        decisionType: 'approve',
        awardedPercent: recommendedPercent,
        ...buildExternalParentEmailApprovalPayload(Boolean(options.sendExternalParentEmail)),
      });
      setOptimisticStatusToken(normalizeStatusToken(asRecord(result as unknown).status || 'approved'));
      setToast({
        tone: 'success',
        message: options.sendExternalParentEmail
          ? 'Application approved and parent email queued.'
          : 'Application successfully approved.',
      });
      closeCompleteModal();
      refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to approve the application right now.';
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }
  };

  const handleDeny = async (options: DecisionSubmitOptions = {}) => {
    if (!applicationId) return;
    setBusyAction('deny');
    try {
      const result = await makeDecision.mutateAsync({
        applicationId,
        decisionType: 'deny',
        denialReason: 'staff_review',
        ...buildExternalParentEmailApprovalPayload(Boolean(options.sendExternalParentEmail)),
      });
      setOptimisticStatusToken(normalizeStatusToken(asRecord(result as unknown).status || 'denied'));
      setToast({
        tone: 'success',
        message: options.sendExternalParentEmail
          ? 'Application declined and parent email queued.'
          : 'Application successfully declined.',
      });
      closeCompleteModal();
      refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to decline the application right now.';
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }
  };

  const handleQueueDecisionEmail = () => {
    if (!decisionEmailPreview) return;
    if (!decisionEmailConfirmed) {
      setToast({ tone: 'error', message: 'Please confirm the email preview before queueing a parent email.' });
      return;
    }
    if (!decisionEmailRecipient) {
      setToast({ tone: 'error', message: 'Add a parent email before queueing this message.' });
      return;
    }

    if (decisionEmailPreview.kind === 'approve') {
      handleApprove({ sendExternalParentEmail: true });
      return;
    }

    handleDeny({ sendExternalParentEmail: true });
  };

  const handleContactSend = () => {
    const subject = readText(contactSubject, `Financial Aid Update - ${familyName}`);
    const message = readText(contactMessage);
    if (!message) {
      setToast({ tone: 'error', message: 'Please enter a message before sending.' });
      return;
    }
    if (!primaryEmail) {
      setToast({ tone: 'error', message: 'Primary contact email is not available for this application.' });
      return;
    }
    if (typeof window === 'undefined') {
      setToast({ tone: 'error', message: 'Unable to open your mail client in this environment.' });
      return;
    }
    const mailto = `mailto:${encodeURIComponent(primaryEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
    window.open(mailto, '_blank', 'noopener,noreferrer');
    setToast({ tone: 'success', message: 'Draft opened in your mail client.' });
    setContactSubject('');
    setContactMessage('');
    setIsContactModalOpen(false);
  };

  useEffect(() => {
    if (!toast) return () => {};
    const timeoutId = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [toast]);

  useEffect(() => {
    setOptimisticStatusToken(null);
  }, [applicationId]);

  useEffect(() => {
    if (!isAssignMenuOpen) return () => {};
    const frame = requestAnimationFrame(() => {
      const first = assignMenuRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])');
      first?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [isAssignMenuOpen]);

  useEffect(() => {
    const node = detailRootRef.current;
    if (!node) return () => {};

    // INT-804: measure the parent surface, not the detail root itself. Compact
    // mode caps the root at max-w-[760px], so measuring the root would keep
    // reporting <=760px and latch the compact layout after the viewport
    // returns to desktop width.
    const surface = node.parentElement ?? node;

    const syncWidth = () => {
      const surfaceWidth = Math.round(surface.getBoundingClientRect().width);
      setDetailSurfaceWidth(surfaceWidth);
    };

    syncWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncWidth);
      return () => window.removeEventListener('resize', syncWidth);
    }

    const observer = new ResizeObserver(syncWidth);
    observer.observe(surface);
    return () => observer.disconnect();
  }, []);
  return (
    <section
      ref={detailRootRef}
      data-testid="financial-aid-staff-detail-root"
      data-extracted-from="owned:ystnj/financial-aid/v2-1/staff-detail"
      data-loaded={review ? 'true' : 'false'}
      data-layout-mode={layoutMode}
      className={`flex ${rootLayoutClass} ${rootWidthClass} bg-background-light text-irongrey font-sans relative min-h-[calc(100vh-64px)]`}
    >
      {toast && typeof document !== 'undefined'
        ? createPortal(
          <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} />,
          document.body
        )
        : null}

      <div className={`${mobileHeaderClass} flex-col bg-background-light border-b border-gray-200 sticky top-0 z-40`}>
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/school/financialaid/staff')}
            className="flex items-center gap-1 text-xs font-bold text-irongrey/60 hover:text-primary transition-colors"
          >
            <ArrowLeft size={14} />
            Back
          </button>
          <div className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: (statusConfig[displayStatusToken] ?? statusConfig.NEW).dot }}
            />
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: (statusConfig[displayStatusToken] ?? statusConfig.NEW).text }}
            >
              {toStatusLabel(displayStatusToken)}
            </span>
          </div>
        </div>
        <div className="px-4 pb-3">
          <ModuleSectionNav
            items={sectionNavItems}
            activeId={activeTab.id}
            ariaLabel="Financial aid detail sections"
            compact={isCompactLayout}
            renderDesktop={false}
            triggerClassName="border-0 active:bg-gray-50"
            mobileMenuTitle="Navigate To..."
            mobileCloseLabel="Close mobile navigation menu"
            testIds={{
              mobileTrigger: 'financial-aid-detail-mobile-menu-trigger',
              mobileDialog: 'financial-aid-detail-mobile-menu',
              mobileItemPrefix: 'financial-aid-detail-mobile-nav',
            }}
          />
        </div>
      </div>

      <nav
        data-testid="financial-aid-detail-sidebar"
        className={`${sidebarClass} w-[200px] bg-background-light border-r flex-shrink-0 overflow-y-auto flex-col px-2`}
        style={{ borderColor: '#F3F4F6' }}
      >
        <button
          type="button"
          onClick={() => navigate('/school/financialaid/staff')}
          className="flex items-center gap-1 text-sm mb-8 font-medium px-2 text-left"
          style={{ color: '#404F4D' }}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="space-y-1">
          {sidebarTabs.map((tab) => (
            <NavLink
              key={tab.id}
              to={tab.id}
              data-testid={`financial-aid-detail-tab-${tab.id}`}
              style={{ fontSize: "smaller" }}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer transition-colors mb-1 ${isActive ? 'bg-white text-jungle font-bold shadow-sm' : 'text-[#6B7280] hover:bg-[#ffffffd1]'}`}
            >
              <tab.icon size={18} />
              <span className="flex-1">{tab.label}</span>
              {tab.badge ? (
                <span className="ml-auto text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center text-white bg-vibrant-coral shadow-sm shadow-vibrant-coral/20">
                  {tab.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header
          data-testid="financial-aid-detail-header"
          className={`${headerPaddingClass} border-b bg-background-light flex-shrink-0`}
          style={{ borderColor: '#F3F4F6', paddingTop: 0 }}
        >
          <h1 className="sr-only">Financial Aid application detail: {familyName}</h1>
          <div className={`flex ${headerContentClass} justify-between gap-4`}>
            <div className="min-w-0 flex-1">
              <div className={`flex min-w-0 max-w-full ${titleStatusRowClass}`}>
                <h2
                  data-testid="financial-aid-detail-heading"
                  className={`${titleClass} ${titleWidthClass} whitespace-normal break-normal font-bold text-[#111827]`}
                  title={familyName}
                >
                  {familyName}
                </h2>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: (statusConfig[displayStatusToken] ?? statusConfig.NEW).dot }}
                  />
                  <span
                    className="text-xs font-semibold uppercase tracking-wide"
                    style={{ color: (statusConfig[displayStatusToken] ?? statusConfig.NEW).text }}
                  >
                    {toStatusLabel(displayStatusToken)}
                  </span>
                </div>
              </div>
              <p className={`${metaClass} mt-1`} style={{ color: '#9CA3AF' }}>
                REF: {code} {submittedDate ? `• Submitted ${submittedDate}` : ''}
              </p>
            </div>
            <div className={`flex items-center ${actionRowClass} relative min-w-0 flex-wrap`}>
              {isCorrectionEditing ? (
                <>
                  <button
                    type="button"
                    onClick={resetCorrectionDraft}
                    disabled={updateApplication.isPending}
                    className={`flex items-center gap-2 text-[#404F4D] font-medium hover:text-[#111827] transition-all bg-white border border-[#E5E7EB] rounded-lg whitespace-nowrap ${actionButtonClass}`}
                  >
                    <X size={16} />
                    <span>Cancel</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveCorrection}
                    disabled={correctionSaveDisabled}
                    className={`flex items-center gap-2 font-medium transition-all rounded-lg whitespace-nowrap ${actionButtonClass} ${
                      correctionSaveDisabled
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-primary-hover'
                    }`}
                  >
                    <Save size={16} />
                    <span>{updateApplication.isPending ? 'Saving...' : 'Save'}</span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={beginCorrectionEditing}
                  className={`flex items-center gap-2 text-primary font-medium hover:text-primary-hover transition-all bg-primary/5 rounded-lg whitespace-nowrap ${actionButtonClass}`}
                >
                  <Pencil size={16} />
                  <span>Edit</span>
                </button>
              )}
              <div className="relative">
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={isAssignMenuOpen}
                  onClick={() => setIsAssignMenuOpen((open) => !open)}
                  className={`flex items-center gap-2 text-primary font-medium hover:text-primary-hover transition-all bg-primary/5 rounded-lg whitespace-nowrap ${actionButtonClass}`}
                >
                  <UserPlus size={16} />
                  <span className="min-w-0 max-w-[10rem] truncate">{assigneeName || 'Assign to...'}</span>
                </button>
                {isAssignMenuOpen ? (
                  <div
                    ref={assignMenuRef}
                    role="menu"
                    aria-label="Assign financial aid application"
                    onKeyDown={handleAssignMenuKeyDown}
                    className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-gray-100 bg-white py-1.5 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)]"
                  >
                    <div className="max-h-[280px] overflow-y-auto">
                      {assigneeName ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => void handleAssignTo(null)}
                          className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 text-[#6B7280]"
                        >
                          Unassign
                        </button>
                      ) : null}
                      {staffAssignOptions.length > 0 ? (
                        staffAssignOptions.map((option) => (
                          <button
                            key={option.name}
                            type="button"
                            role="menuitem"
                            disabled={!option.id}
                            title={!option.id ? 'Staff record does not include a valid user ID' : undefined}
                            onClick={() => void handleAssignTo(option)}
                            className={`w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${assigneeName === option.name ? 'text-primary bg-primary/5' : 'text-[#111827]'}`}
                          >
                            {option.name}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-xs text-[#6B7280]">
                          Staff assignments are syncing.
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setIsContactModalOpen(true)}
                className={`flex items-center gap-2 text-primary font-medium hover:text-primary-hover transition-all bg-primary/5 rounded-lg whitespace-nowrap ${actionButtonClass}`}
              >
                <MailPlus size={16} />
                <span className="hidden sm:inline">Contact</span>
                <span className="sm:hidden">Msg</span>
              </button>
              <button
                type="button"
                onClick={() => setIsCompleteModalOpen(true)}
                disabled={busyAction != null || isCorrectionEditing}
                className={`flex items-center gap-2 text-primary font-medium transition-all bg-primary/5 rounded-lg whitespace-nowrap ${actionButtonClass} ${busyAction || isCorrectionEditing ? 'opacity-70 cursor-not-allowed' : 'hover:text-primary-hover'}`}
              >
                <CheckCircle size={16} />
                <span className="hidden sm:inline">Complete</span>
                <span className="sm:hidden">Done</span>
              </button>
            </div>
          </div>
          {isCorrectionEditing && !isCorrectionsTabActive ? correctionEditor : null}
          {detail.isLoading ? (
            <p className="text-xs text-[#6B7280] mt-3">Loading application...</p>
          ) : null}
          {detail.error ? (
            <SupportErrorMessage className="mt-3">
              Unable to load this application.
            </SupportErrorMessage>
          ) : null}
        </header>

        <main className={`flex-1 overflow-y-auto ${mainPaddingClass} bg-background-light`}>
          <Outlet context={ctx} />
        </main>
      </div>

      {isCompleteModalOpen ? (
        <AppDialogFrame
          open={isCompleteModalOpen}
          onClose={closeCompleteModal}
          title="Complete Application"
          description={`Choose an outcome for the ${familyName} family application.`}
          testId="financial-aid-complete-application-modal"
          maxWidthClassName="max-w-2xl"
          titleClassName="text-xl font-semibold text-[#111827]"
          descriptionClassName="text-sm text-[#6B7280]"
          bodyClassName="space-y-4"
          bodyPaddingClassName="p-8"
          footerPaddingClassName="px-8 py-6"
          footerClassName="justify-center"
          footer={(
            <button
              type="button"
              className="text-sm text-[#404F4D] hover:underline"
              onClick={closeCompleteModal}
            >
              Cancel
            </button>
          )}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              className="group w-full rounded-xl border-2 border-[#E5E7EB] p-5 text-left transition-all hover:border-[#28E2A7] hover:bg-[#ECFDF5] disabled:cursor-not-allowed"
              onClick={() => handleApprove()}
              disabled={busyAction != null}
            >
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-[#ECFDF5] p-2 text-[#28E2A7] transition-colors group-hover:bg-white">
                  <CheckCircle size={24} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#111827]">Approve</h3>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    Award scholarship at {Math.max(0, Math.round(recommendedPercent))}% recommendation. No parent email is queued.
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              className="group w-full rounded-xl border-2 border-[#E5E7EB] p-5 text-left transition-all hover:border-[#FF5F5F] hover:bg-[#FEE2E2] disabled:cursor-not-allowed"
              onClick={() => handleDeny()}
              disabled={busyAction != null}
            >
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-[#FEE2E2] p-2 text-[#FF5F5F] transition-colors group-hover:bg-white">
                  <XCircle size={24} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#111827]">Decline</h3>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    Deny this financial aid application. No parent email is queued.
                  </p>
                </div>
              </div>
            </button>
          </div>

          <div className="rounded-xl border-2 border-[#E5E7EB] p-5">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-[#FEF3C7] p-2 text-[#D97706]">
                <MailPlus size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-[#111827]">Request More Info</h3>
                <p className="mt-1 text-sm text-[#6B7280]">
                  Move this application to More Info Needed and notify the family what to provide.
                </p>
                {isMoreInfoMode ? (
                  <div className="mt-3 space-y-3">
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
                        Requested information
                        <span className="ml-2 rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[9px] text-[#92400E]">
                          Required
                        </span>
                      </span>
                      <textarea
                        value={moreInfoMessage}
                        onChange={(event) => {
                          setMoreInfoMessage(event.target.value);
                          setMoreInfoEmailConfirmed(false);
                          setMoreInfoErrorMessage('');
                        }}
                        rows={3}
                        required
                        aria-required="true"
                        data-testid="financial-aid-detail-more-info-message"
                        placeholder="Describe the documents or details the family should provide..."
                        className="mt-2 w-full rounded-lg border border-[#E5E7EB] p-3 text-sm text-[#111827] focus:border-primary focus:outline-none"
                      />
                    </label>
                    {moreInfoErrorMessage ? (
                      <SupportErrorMessage
                        className="mt-3"
                        data-testid="financial-aid-detail-more-info-error"
                        showSupport={false}
                      >
                        {moreInfoErrorMessage}
                      </SupportErrorMessage>
                    ) : null}
                    <div
                      className="space-y-3 rounded-lg border border-[#E5E7EB] bg-white p-4"
                      data-testid="more-info-email-preview"
                    >
                      <p className="text-xs text-[#6B7280]">
                        Review the parent-facing message before queueing an external email.
                      </p>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Recipient</p>
                        <p className="mt-1 text-sm font-semibold text-[#111827]">{moreInfoEmailPreview.to}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Subject</p>
                        <p className="mt-1 text-sm font-semibold text-[#111827]">{moreInfoEmailPreview.subject}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Message</p>
                        <p className="mt-1 whitespace-pre-line text-sm text-[#6B7280]">{moreInfoEmailPreview.message}</p>
                      </div>
                      <label className="flex items-start gap-3 rounded-lg border border-[#E5E7EB] bg-[#FAFBF8] p-3 text-sm text-[#404F4D]">
                        <input
                          type="checkbox"
                          checked={moreInfoEmailConfirmed}
                          onChange={(event) => setMoreInfoEmailConfirmed(event.currentTarget.checked)}
                          className="mt-1 h-4 w-4 rounded border-[#D1D5DB] text-primary focus:ring-primary"
                        />
                        <span>
                          I reviewed this parent-facing message and approve queueing an external email for this
                          more-info request.
                        </span>
                      </label>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => void handleRequestMoreInfo()}
                        disabled={busyAction != null}
                        className="rounded-lg border border-primary/20 bg-white px-4 py-2 text-sm font-semibold text-primary transition-colors hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {busyAction === 'more-info' ? 'Saving...' : 'Move without email'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleRequestMoreInfo({ sendExternalParentEmail: true })}
                        disabled={busyAction != null || !moreInfoEmailConfirmed || !decisionEmailRecipient}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                          busyAction != null || !moreInfoEmailConfirmed || !decisionEmailRecipient
                            ? 'cursor-not-allowed bg-gray-300 text-white'
                            : 'bg-primary text-white hover:bg-primary-hover'
                        }`}
                      >
                        <Send size={16} />
                        {busyAction === 'more-info' ? 'Queueing...' : 'Queue Email'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsMoreInfoMode(false);
                          setMoreInfoMessage('');
                          setMoreInfoEmailConfirmed(false);
                          setMoreInfoErrorMessage('');
                        }}
                        disabled={busyAction != null}
                        className="text-sm text-[#404F4D] hover:underline disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMoreInfoMode(true);
                      setMoreInfoEmailConfirmed(false);
                      setMoreInfoErrorMessage('');
                    }}
                    disabled={busyAction != null}
                    className="mt-3 rounded-lg border border-[#E5E7EB] px-4 py-2 text-sm font-semibold text-[#111827] transition-colors hover:border-[#D97706] hover:bg-[#FFFBEB] disabled:cursor-not-allowed"
                  >
                    Request more info...
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-[#E5E7EB] bg-[#FAFBF8] p-4">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-bold text-[#111827]">Optional Parent Email</h3>
              <p className="text-xs text-[#6B7280]">
                Queue a parent-facing decision email only after reviewing the same recipient and message fields used by the outbound status email.
              </p>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => openDecisionEmailPreview('approve')}
                disabled={busyAction != null}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed ${
                  decisionEmailPreviewKind === 'approve'
                    ? 'border-[#28E2A7] bg-[#ECFDF5] text-[#237062]'
                    : 'border-[#E5E7EB] bg-white text-[#404F4D] hover:border-[#28E2A7]'
                }`}
              >
                Preview approval email
              </button>
              <button
                type="button"
                onClick={() => openDecisionEmailPreview('deny')}
                disabled={busyAction != null}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed ${
                  decisionEmailPreviewKind === 'deny'
                    ? 'border-[#FF5F5F] bg-[#FEE2E2] text-[#FE2C3B]'
                    : 'border-[#E5E7EB] bg-white text-[#404F4D] hover:border-[#FF5F5F]'
                }`}
              >
                Preview denial email
              </button>
            </div>

            {decisionEmailPreview ? (
              <div
                className="mt-4 space-y-3 rounded-lg border border-[#E5E7EB] bg-white p-4"
                data-testid="decision-email-preview"
              >
                <div className="grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Recipient</p>
                    <p className="mt-1 font-semibold text-[#111827]">{decisionEmailPreview.to}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Current Step</p>
                    <p className="mt-1 font-semibold text-[#111827]">{decisionEmailPreview.statusLabel}</p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Subject</p>
                  <p className="mt-1 text-sm font-semibold text-[#111827]">{decisionEmailPreview.subject}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Message</p>
                  <p className="mt-1 text-sm text-[#6B7280]">{decisionEmailPreview.message}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Action</p>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    <span className="font-semibold text-[#111827]">{decisionEmailPreview.actionHeading}:</span>{" "}
                    {decisionEmailPreview.actionBody}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-[#237062]">{decisionEmailPreview.actionLabel}</p>
                </div>
                <label className="flex items-start gap-3 rounded-lg border border-[#E5E7EB] bg-[#FAFBF8] p-3 text-sm text-[#404F4D]">
                  <input
                    type="checkbox"
                    checked={decisionEmailConfirmed}
                    onChange={(event) => setDecisionEmailConfirmed(event.currentTarget.checked)}
                    className="mt-1 h-4 w-4 rounded border-[#D1D5DB] text-primary focus:ring-primary"
                  />
                  <span>I reviewed the recipient and parent-facing message preview and approve queueing this parent email.</span>
                </label>
                <button
                  type="button"
                  onClick={handleQueueDecisionEmail}
                  disabled={busyAction != null || !decisionEmailConfirmed || !decisionEmailRecipient}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all ${
                    busyAction != null || !decisionEmailConfirmed || !decisionEmailRecipient
                      ? 'cursor-not-allowed bg-gray-300 text-white'
                      : 'bg-primary text-white hover:bg-primary-hover'
                  }`}
                >
                  <Send size={16} />
                  {decisionEmailPreview.kind === 'approve' ? 'Approve and queue email' : 'Decline and queue email'}
                </button>
              </div>
            ) : null}
          </div>
        </AppDialogFrame>
      ) : null}

      {isContactModalOpen ? (
        <AppDialogFrame
          open={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          title="Contact Family"
          description="Send a message to the primary contact."
          testId="financial-aid-contact-family-modal"
          maxWidthClassName="max-w-lg"
          panelClassName="rounded-3xl"
          titleClassName="text-2xl font-bold text-[#111827]"
          descriptionClassName="text-sm font-medium text-[#9CA3AF]"
          bodyClassName="space-y-6"
          bodyPaddingClassName="px-8 pb-8 pt-0"
          footerPaddingClassName="px-8 py-6"
          footerClassName="justify-between"
          footer={(
            <>
              <button
                type="button"
                onClick={() => setIsContactModalOpen(false)}
                className="text-sm font-bold text-irongrey/40 transition-colors hover:text-irongrey"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleContactSend}
                disabled={!contactSubject.trim() || !contactMessage.trim()}
                className={`flex items-center gap-2 rounded-2xl px-8 py-3 text-sm font-bold shadow-lg transition-all ${!contactSubject.trim() || !contactMessage.trim() ? 'cursor-not-allowed bg-gray-300 text-white' : 'bg-primary text-white shadow-primary/20 hover:bg-primary-hover'}`}
              >
                <Send size={18} />
                Send Message
              </button>
            </>
          )}
        >
          <div className="rounded-2xl border border-gray-100 bg-[#FAFBF8] p-6">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Primary Contact</p>
            <h3 className="text-lg font-bold text-[#111827]">{primaryContactName}</h3>
            <div className="mt-2 space-y-0.5 text-sm text-[#6B7280]">
              <p>{primaryEmail}</p>
              <p>{primaryPhone}</p>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Subject Line</label>
            <input
              type="text"
              value={contactSubject}
              onChange={(event) => setContactSubject(event.currentTarget.value)}
              className="w-full rounded-xl border border-gray-100 bg-[#FAFBF8] px-4 py-3 text-sm text-irongrey placeholder:text-irongrey/30 focus:border-primary focus:ring-primary"
              placeholder="Enter subject..."
            />
          </div>

          <div>
            <label className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Message</label>
            <textarea
              value={contactMessage}
              onChange={(event) => setContactMessage(event.currentTarget.value)}
              className="min-h-[160px] w-full resize-none rounded-xl border border-gray-100 bg-[#FAFBF8] p-4 text-sm text-irongrey focus:border-primary focus:ring-primary"
              placeholder="Type your message here..."
            />
          </div>
        </AppDialogFrame>
      ) : null}
    </section>
  );
}
